import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
    getDeliveryAddress,
    isReferralProgram,
    sentryLogError,
    getFinishRequestData,
    getDeliveryTypeStatus,
    getAdditionalInfoVariation,
} from '../../utils';
import type { TDeliveryType, TOfficeOption, IFinishResponse } from '../../models';
import { BROKERAGE_ACCOUNT } from '../../constants';
import { api } from '../../api';

import type { IFinishSlice, TCreateThunkApi } from './models';
import { setDeliveryScreenStatus, getDeliveryTypes, setIsNeedReselectTimeSlot } from './delivery';
import { deleteDraftCookie } from './draft';
import { setStep, setStatus, setErrorStep, setChangePhoneButtonVisible } from './application';

const initialState: IFinishSlice = {
    deliveryAddress: undefined,
    deliveryTypeStatus: 'COURIER_DELIVERY',
    selectedTimeSlot: undefined,
    referralOffers: undefined,
    additionalInfoVariation: 'default',
};

export const finishSlice = createSlice({
    name: 'finish',
    initialState,
    reducers: {
        setDeliveryAddress: (state, action: PayloadAction<IFinishSlice['deliveryAddress']>) => {
            state.deliveryAddress = action.payload;
        },
        setDeliveryTypeStatus: (
            state,
            action: PayloadAction<IFinishSlice['deliveryTypeStatus']>
        ) => {
            state.deliveryTypeStatus = action.payload;
        },
        setSelectedTimeSlot: (state, action: PayloadAction<IFinishSlice['selectedTimeSlot']>) => {
            state.selectedTimeSlot = action.payload;
        },
        setReferralOffers: (state, action: PayloadAction<IFinishSlice['referralOffers']>) => {
            state.referralOffers = action.payload;
        },
        resetFinish: () => ({ ...initialState }),
        setAdditionalInfoVariation: (
            state,
            action: PayloadAction<IFinishSlice['additionalInfoVariation']>
        ) => {
            state.additionalInfoVariation = action.payload;
        },
    },
});

export const {
    resetFinish,
    setAdditionalInfoVariation,
    setDeliveryAddress,
    setDeliveryTypeStatus,
    setReferralOffers,
    setSelectedTimeSlot,
} = finishSlice.actions;

/** Завершение заявки */
export const handleFinishOrder = createAsyncThunk<void, IFinishResponse, TCreateThunkApi>(
    'handleFinishOrder',
    (resData, { dispatch, getState }) => {
        if (isReferralProgram(resData) && resData.referralProgram?.offers) {
            const referralOffers = resData.referralProgram.offers;

            dispatch(setReferralOffers(referralOffers));
        }

        if (resData.passportBirthDate) {
            const {
                application: { brokerageCheckbox },
                basket: { availableServices },
            } = getState();

            const additionalInfoVariation = brokerageCheckbox
                ? getAdditionalInfoVariation(resData.passportBirthDate, availableServices)
                : 'default';

            dispatch(setAdditionalInfoVariation(additionalInfoVariation));
        }

        dispatch(setStep('thanksPage'));
        dispatch(deleteDraftCookie());
    }
);

/** Запрос на завершение заказа */
export const finishOrderRequest = createAsyncThunk<
    void,
    {
        brokerageAccountRequired?: boolean;
        codeWord?: string;
        comment?: string;
        deliveryType: TDeliveryType;
        selectedOffice?: TOfficeOption;
    },
    TCreateThunkApi
>('finishOrderRequest', async (args, { dispatch, getState }) => {
    const {
        application: { appId, businessUid },
        basket: { errorProductsIds = [], products, services },
        delivery: { clientAddress, courier, officeDeliveryType, offices },
        finish: { selectedTimeSlot },
    } = getState();

    dispatch(setDeliveryScreenStatus('finishLoading'));

    const { brokerageAccountRequired, codeWord, comment, deliveryType, selectedOffice } = args;

    const deliveryAddress = getDeliveryAddress(
        deliveryType,
        clientAddress?.fullName || undefined,
        selectedOffice
    );

    // TODO: вынести логику корзины из финиша - https://jira.moscow.alfaintra.net/browse/SITEDK-4826
    // Фильтруем продукты, чтобы исключить те, которые недоступны для выбора
    const filteredProducts = products.filter(productId => !errorProductsIds.includes(productId));

    // TODO: вынести логику корзины из финиша - https://jira.moscow.alfaintra.net/browse/SITEDK-4826
    // Фильтруем сервисы, чтобы исключить те, которые недоступны для выбора
    const filteredServices = services.filter(serviceId => !errorProductsIds.includes(serviceId));

    const data = {
        appId,
        businessUid,
        codeWord,
        comment,
        deliveryAddress,
        courier,
        offices,
        deliveryType,
        officeDeliveryType,
        selectedTimeSlot,
        selectedOffice,
        brokerageAccountRequired,
        additionalProducts: filteredProducts,
        additionalServices: brokerageAccountRequired
            ? [...filteredServices, BROKERAGE_ACCOUNT]
            : filteredServices,
    };

    const requestData = getFinishRequestData(data);

    /* istanbul ignore if */
    if (!requestData) {
        console.error(data);

        sentryLogError({
            message: 'applicationsFinish Ошибка получения данных для запроса',
            data: { ...data, appId },
        });

        dispatch(setErrorStep('INTERNAL_SERVER_ERROR'));

        return;
    }

    try {
        const deliveryTypeStatus = getDeliveryTypeStatus(requestData);

        dispatch(setDeliveryTypeStatus(deliveryTypeStatus));

        const { data: resData } = await api.finishOrder(requestData);

        dispatch(setDeliveryAddress(deliveryAddress));

        if (resData.status === 'NEED_SMS') {
            dispatch(setChangePhoneButtonVisible(false));
            dispatch(setStep('smsConfirm'));
            dispatch(setStatus(resData.status));

            return;
        }

        dispatch(handleFinishOrder(resData));
    } catch (error) {
        if (!axios.isAxiosError(error)) {
            dispatch(setErrorStep());

            sentryLogError({
                error,
                message: 'applicationsFinish error',
                data: { ...requestData, appId },
            });

            return;
        }

        if (error?.response?.status === 410) {
            dispatch(getDeliveryTypes());
            dispatch(setIsNeedReselectTimeSlot(true));

            return;
        }

        dispatch(setErrorStep(error?.response?.data?.status));

        sentryLogError({
            error,
            message: 'applicationsFinish error',
            data: { ...requestData, appId },
        });
    } finally {
        dispatch(setDeliveryScreenStatus('idle'));
    }
});
